import { type Dispatch, type SetStateAction, type MouseEvent, useState, JSX } from "react";

import { MoreHoriz } from "@mui/icons-material";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { APP_BASENAME } from "src/lib/config.helper";
import { updateUserPinnedLocations } from "src/lib/settings.helper";
import useAppPinnedStore from "src/store/pinned.store";
import LocationType from "src/types/LocationType";

const styles = {
  icon: { display: "flex", justifyContent: "center", alignItems: "center" },
  menu: { minWidth: 150 },
};

const handleOpenInNewTab = (locationId: string, type: LocationType): void => {
  ReactGA.event("open_new_tab", {
    category: "Navigation",
    label: locationId,
  });

  if (type === LocationType.Shop) {
    window.open(`${APP_BASENAME}/shop-dashboard/${locationId}`, "_blank");
  }
  if (type === LocationType.Mall) {
    window.open(`${APP_BASENAME}/center-dashboard/${locationId}`, "_blank");
  }
  if (type === LocationType.Station) {
    window.open(`${APP_BASENAME}/station-dashboard/${locationId}`, "_blank");
  }
};

function PinnedItemMenu({
  locationId,
  type,
  setError = null,
  pin = false,
}: {
  locationId: string;
  type: LocationType;
  setError?: Dispatch<SetStateAction<string | null>> | null;
  pin?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { appPinned, setAppPinned } = useAppPinnedStore();

  const handleRemoveFromPinned = async (locationId: string): Promise<void> => {
    ReactGA.event("unpin_location", {
      category: "Dashboard",
      label: locationId,
    });

    const updatedAppPinned = appPinned.filter((pinnedItem) => pinnedItem.id !== locationId);
    const updatedFromBackend = await updateUserPinnedLocations(updatedAppPinned);
    setAppPinned(updatedFromBackend);
  };

  const handleAddToPinned = async (locationId: string, locationType: LocationType): Promise<void> => {
    ReactGA.event("pin_location", {
      category: "Dashboard",
      label: locationId,
    });

    const updatedAppPinned = [...appPinned, { id: locationId, type: locationType }];

    try {
      const updatedFromBackend = await updateUserPinnedLocations(updatedAppPinned);
      setAppPinned(updatedFromBackend);
    } catch (error) {
      if (setError) {
        const msg = (error as Error).message || "";

        if (msg.includes("The maximum limit for Pinned")) {
          setError("You can pin up to five dashboards. Unpin one to add another.");
        } else {
          setError(`Error while updating pins: ${msg}`);
        }
      }
    }
  };

  const handleMenuOpen = (event: MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event: MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  return (
    <Box>
      <IconButton onClick={handleMenuOpen} size='small' sx={styles.icon}>
        <MoreHoriz />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} sx={styles.menu}>
        <MenuItem
          className='medium'
          onClick={(e) => {
            handleMenuClose(e);
            ReactGA.event("open_dashboard", {
              category: "Navigation",
              label: locationId,
            });

            if (type === LocationType.Shop) {
              navigate(`/shop-dashboard/${locationId}`);
            }
            if (type === LocationType.Mall) {
              navigate(`/center-dashboard/${locationId}`);
            }
            if (type === LocationType.Station) {
              navigate(`/station-dashboard/${locationId}`);
            }
          }}
        >
          {t("Open")}
        </MenuItem>
        <MenuItem
          className='medium'
          onClick={(e) => {
            handleMenuClose(e);
            handleOpenInNewTab(locationId, type);
          }}
        >
          {t("Open in new tab")}
        </MenuItem>
        <MenuItem
          className='medium'
          disabled={
            pin &&
            appPinned
              .filter((item) => item.type === type)
              .map((item) => item.id)
              .includes(locationId)
          }
          onClick={(e) => {
            handleMenuClose(e);
            if (pin) {
              void handleAddToPinned(locationId, type);
            } else {
              handleRemoveFromPinned(locationId);
            }
          }}
        >
          {pin ? t("Pin") : t("Remove from pinned")}
        </MenuItem>
      </Menu>
    </Box>
  );
}

PinnedItemMenu.defaultProps = {
  setError: null,
  pin: false,
};

export default PinnedItemMenu;
